import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Button, Card, Cluster, FormField, Input, Select, Stack } from "@salesapp/ui";
import { TipHeading } from "./AdminTip";
import { Flash } from "./Flash";
import { api } from "./api";
import { notifySaved } from "./savedToast";
import { formatAiUsd, money } from "./format";
import { leadgenPlan, loxleyPlan, mailboxPlan, type PlanResult } from "./aiAllowanceMath";

export interface CalculatorInputs {
  measure_days: number;
  pricing_known: boolean;
  fx: { base: string; rates: Record<string, number> };
  offer: {
    status: string;
    currency: string;
    leadgen_cents: number | null;
    loxley_seat_cents: number | null;
    loxley_seats: number | null;
    mailbox_cents: number | null;
  } | null;
  leadgen: { monthly_limit: number; leads_measured: number; cost_per_lead_usd: number | null };
  loxley: {
    seats: number;
    requests_measured: number;
    cost_per_request_usd: number | null;
    platform_cost_per_request_usd: number | null;
    requests_per_seat_month: number | null;
  };
  /** Mailbox AI is a flat fee; it is sized by emails the model sorts a month. */
  mailbox: {
    emails_measured: number;
    cost_per_email_usd: number | null;
    platform_cost_per_email_usd: number | null;
    emails_per_month: number | null;
  };
  budget: { monthly_budget_cents: number; feature_caps_cents: Record<string, number | null> };
}

/** Requests per seat to size Loxley's limit for when nothing is measured: the old per-seat command limit. */
const DEFAULT_REQUESTS_PER_SEAT = 300;

/** Figures for the guide page, where no customer is open. Clearly examples, not measurements. */
export const EXAMPLE_INPUTS: CalculatorInputs = {
  measure_days: 90,
  pricing_known: true,
  fx: { base: "USD", rates: { AUD: 1.43 } },
  offer: {
    status: "example", currency: "AUD", leadgen_cents: 4900, loxley_seat_cents: 1500, loxley_seats: 3,
    mailbox_cents: 3000,
  },
  leadgen: { monthly_limit: 55, leads_measured: 0, cost_per_lead_usd: 0.02 },
  loxley: {
    seats: 3, requests_measured: 0, cost_per_request_usd: 0.004, platform_cost_per_request_usd: null,
    requests_per_seat_month: null,
  },
  mailbox: { emails_measured: 0, cost_per_email_usd: 0.001, platform_cost_per_email_usd: null, emails_per_month: 2000 },
  budget: { monthly_budget_cents: 1000, feature_caps_cents: { leadgen: null, loxley: null, mailbox: null } },
};

function num(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function cents(value: string): number {
  return Math.round(num(value) * 100);
}

function dollars(value: number | null | undefined): string {
  return value == null ? "" : String(value / 100);
}

function margin(plan: PlanResult, target: number): string {
  if (plan.marginPct == null) return "—";
  const label = `${plan.marginPct.toFixed(1)}%`;
  return plan.marginPct < target ? `${label} (below the ${target}% target)` : label;
}

/**
 * Size the AI add-ons: how many leads a price buys, and the cost limit LeadGen, Loxley and
 * Mailbox AI each need so the customer can use all of it. `orgId` loads that customer's
 * prices and measured costs.
 */
export function AllowanceCalculator({ orgId }: { orgId?: string }) {
  const [inputs, setInputs] = useState<CalculatorInputs | null>(orgId ? null : EXAMPLE_INPUTS);
  const [error, setError] = useState<string | null>(null);
  const [currency, setCurrency] = useState("USD");
  const [fx, setFx] = useState("1");
  const [target, setTarget] = useState("70");
  const [headroom, setHeadroom] = useState("25");
  const [cardPrice, setCardPrice] = useState("");
  const [cardLeads, setCardLeads] = useState("");
  const [leadPrice, setLeadPrice] = useState("");
  const [leadCost, setLeadCost] = useState("");
  const [seatPrice, setSeatPrice] = useState("");
  const [seats, setSeats] = useState("");
  const [requests, setRequests] = useState(String(DEFAULT_REQUESTS_PER_SEAT));
  const [requestCost, setRequestCost] = useState("");
  const [mailPrice, setMailPrice] = useState("");
  const [emails, setEmails] = useState("");
  const [emailCost, setEmailCost] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  function draftPayload(): Record<string, string> {
    return {
      target, headroom, currency, fx, cardPrice, cardLeads, leadPrice, leadCost,
      seatPrice, seats, requests, requestCost, mailPrice, emails, emailCost,
    };
  }

  function applyDraft(fields: Record<string, string>) {
    if (fields.target != null) setTarget(fields.target);
    if (fields.headroom != null) setHeadroom(fields.headroom);
    if (fields.currency) setCurrency(fields.currency);
    if (fields.fx != null) setFx(fields.fx);
    if (fields.cardPrice != null) setCardPrice(fields.cardPrice);
    if (fields.cardLeads != null) setCardLeads(fields.cardLeads);
    if (fields.leadPrice != null) setLeadPrice(fields.leadPrice);
    if (fields.leadCost != null) setLeadCost(fields.leadCost);
    if (fields.seatPrice != null) setSeatPrice(fields.seatPrice);
    if (fields.seats != null) setSeats(fields.seats);
    if (fields.requests != null) setRequests(fields.requests);
    if (fields.requestCost != null) setRequestCost(fields.requestCost);
    if (fields.mailPrice != null) setMailPrice(fields.mailPrice);
    if (fields.emails != null) setEmails(fields.emails);
    if (fields.emailCost != null) setEmailCost(fields.emailCost);
  }

  function start(next: CalculatorInputs) {
    // One bad response must not take the rest of the customer page down with this card.
    if (!next?.leadgen || !next.loxley || !next.budget?.feature_caps_cents || !next.fx?.rates) {
      setError("Calculator figures could not be loaded.");
      return;
    }
    setInputs(next);
    const code = next.offer?.currency ?? "USD";
    setCurrency(code);
    setFx(String(code === "USD" ? 1 : next.fx.rates[code] ?? 1));
    setCardPrice(dollars(next.offer?.leadgen_cents));
    setCardLeads(String(next.leadgen.monthly_limit || ""));
    setLeadPrice(dollars(next.offer?.leadgen_cents));
    setLeadCost(next.leadgen.cost_per_lead_usd == null ? "" : String(next.leadgen.cost_per_lead_usd));
    setSeatPrice(dollars(next.offer?.loxley_seat_cents));
    setSeats(String(next.offer?.loxley_seats ?? next.loxley.seats ?? ""));
    const perRequest = next.loxley.cost_per_request_usd ?? next.loxley.platform_cost_per_request_usd;
    setRequestCost(perRequest == null ? "" : String(perRequest));
    setMailPrice(dollars(next.offer?.mailbox_cents));
    const perMonth = next.mailbox?.emails_per_month;
    setEmails(perMonth == null ? "" : String(Math.ceil(perMonth)));
    const perEmail = next.mailbox?.cost_per_email_usd ?? next.mailbox?.platform_cost_per_email_usd;
    setEmailCost(perEmail == null ? "" : String(perEmail));
  }

  useEffect(() => {
    let cancelled = false;
    const scope = orgId ?? "guide";
    void (async () => {
      try {
        const measured = orgId
          ? await api.get<CalculatorInputs>(`/operator/orgs/${orgId}/ai/calculator`)
          : EXAMPLE_INPUTS;
        if (cancelled) return;
        start(measured);
        const draft = await api.get<{ fields?: Record<string, string> | null }>(
          `/operator/ai/calculator-draft?scope=${encodeURIComponent(scope)}`,
        );
        if (!cancelled && draft && draft.fields && typeof draft.fields === "object") applyDraft(draft.fields);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Calculator figures could not be loaded.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  async function saveDraft() {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await api.put("/operator/ai/calculator-draft", { scope: orgId ?? "guide", fields: draftPayload() });
      setNotice("AI cost figures saved.");
      notifySaved("AI cost figures saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "AI cost figures could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  const targetPct = num(target);
  const shared = { fxPerUsd: num(fx), targetMarginPct: targetPct, headroomPct: num(headroom) };
  const lead = leadgenPlan({
    ...shared, rateCardPriceCents: cents(cardPrice), rateCardLeads: num(cardLeads), priceCents: cents(leadPrice),
    costPerLeadUsd: num(leadCost),
  });
  const lox = loxleyPlan({
    ...shared, seatPriceCents: cents(seatPrice), seats: Math.floor(num(seats)), requestsPerSeat: Math.floor(num(requests)),
    costPerRequestUsd: num(requestCost),
  });
  const mail = mailboxPlan({
    ...shared, priceCents: cents(mailPrice), emailsPerMonth: Math.floor(num(emails)), costPerEmailUsd: num(emailCost),
  });
  const suggestedBudget = lead.capCents + lox.capCents + mail.capCents;
  const currencies = ["USD", ...Object.keys(inputs?.fx.rates ?? {}).filter((code) => code !== "USD")];
  const measured = inputs?.measure_days ?? 90;

  return (
    <Card>
      <Stack gap={3}>
        <TipHeading
          label="Allowance calculator"
          tip="Works out how many leads a price buys, and the AI cost limit to set so the customer can use all of it. Save keeps these figures after a deploy. Copy the results into the AI billing, Lead generator, Loxley and Organization AI cards."
        />
        <p className="admin-muted">
          {orgId
            ? `Starts from this customer's latest offer and their measured AI cost over the last ${measured} days.`
            : "Example figures. Open a customer to start from their real prices and measured costs."}{" "}
          <Link to="/ai/pricing-guide">How AI pricing works</Link>
        </p>
        {error && <Flash tone="error">{error}</Flash>}
        {notice && <Flash tone="success">{notice}</Flash>}
        {inputs && !inputs.pricing_known ? (
          <Flash tone="error">
            AI calls are being recorded at $0.00: the platform model has no published price and the AI page has no
            per-million rates. Measured costs below read $0 until rates are set on the AI page.
          </Flash>
        ) : null}
        <Cluster gap={2} align="end">
          <FormField label="Currency" htmlFor="calc-currency" tooltip="The currency the customer is quoted in.">
            <Select
              id="calc-currency"
              value={currency}
              onChange={(event) => {
                const code = event.target.value;
                setCurrency(code);
                setFx(String(code === "USD" ? 1 : inputs?.fx.rates[code] ?? 1));
              }}
            >
              {currencies.map((code) => <option key={code} value={code}>{code}</option>)}
            </Select>
          </FormField>
          <FormField label={`${currency} per US$1`} htmlFor="calc-fx" tooltip="From the Billing FX table. AI cost is charged to Saleslox in US dollars.">
            <Input id="calc-fx" value={fx} onChange={(event) => setFx(event.target.value)} />
          </FormField>
          <FormField label="Target margin %" htmlFor="calc-target" tooltip="Share of the price Saleslox keeps after AI cost, at full use.">
            <Input id="calc-target" value={target} onChange={(event) => setTarget(event.target.value)} />
          </FormField>
          <FormField label="Headroom %" htmlFor="calc-headroom" tooltip="Added on top of the full-use cost when suggesting a cost limit, so a heavy month does not stop the customer early.">
            <Input id="calc-headroom" value={headroom} onChange={(event) => setHeadroom(event.target.value)} />
          </FormField>
        </Cluster>
        <strong>LeadGen</strong>
        <Cluster gap={2} align="end">
          <FormField label={`Rate card price (${currency})`} htmlFor="calc-card-price" tooltip="The reference price, e.g. 49.00 for 55 leads.">
            <Input id="calc-card-price" value={cardPrice} onChange={(event) => setCardPrice(event.target.value)} />
          </FormField>
          <FormField label="Rate card leads" htmlFor="calc-card-leads" tooltip="Leads a month the reference price buys.">
            <Input id="calc-card-leads" value={cardLeads} onChange={(event) => setCardLeads(event.target.value)} />
          </FormField>
          <FormField label={`Customer's price a month (${currency})`} htmlFor="calc-lead-price" tooltip="What this customer will pay, e.g. 70.00.">
            <Input id="calc-lead-price" value={leadPrice} onChange={(event) => setLeadPrice(event.target.value)} />
          </FormField>
          <FormField
            label="AI cost per lead (US$)"
            htmlFor="calc-lead-cost"
            tooltip={`Measured: ${inputs?.leadgen.leads_measured ?? 0} leads in the last ${measured} days. Edit to try another figure.`}
          >
            <Input id="calc-lead-cost" value={leadCost} onChange={(event) => setLeadCost(event.target.value)} />
          </FormField>
        </Cluster>
        <strong>Loxley</strong>
        <Cluster gap={2} align="end">
          <FormField label={`Price per seat a month (${currency})`} htmlFor="calc-seat-price">
            <Input id="calc-seat-price" value={seatPrice} onChange={(event) => setSeatPrice(event.target.value)} />
          </FormField>
          <FormField label="Loxley seats" htmlFor="calc-seats">
            <Input id="calc-seats" value={seats} onChange={(event) => setSeats(event.target.value)} />
          </FormField>
          <FormField
            label="Requests per seat a month"
            htmlFor="calc-requests"
            tooltip={`Size the limit for a heavy user, not the average. Measured average: ${inputs?.loxley.requests_per_seat_month ?? "none yet"}.`}
          >
            <Input id="calc-requests" value={requests} onChange={(event) => setRequests(event.target.value)} />
          </FormField>
          <FormField
            label="AI cost per request (US$)"
            htmlFor="calc-request-cost"
            tooltip={`This customer: ${inputs?.loxley.cost_per_request_usd ?? "none"} over ${inputs?.loxley.requests_measured ?? 0} requests. All customers: ${inputs?.loxley.platform_cost_per_request_usd ?? "none"}.`}
          >
            <Input id="calc-request-cost" value={requestCost} onChange={(event) => setRequestCost(event.target.value)} />
          </FormField>
        </Cluster>
        <strong>Mailbox AI</strong>
        <Cluster gap={2} align="end">
          <FormField
            label={`Price a month (${currency})`}
            htmlFor="calc-mail-price"
            tooltip="A flat fee: Mailbox AI sells no unit, so its limit is sized by the emails the shared mailbox sorts."
          >
            <Input id="calc-mail-price" value={mailPrice} onChange={(event) => setMailPrice(event.target.value)} />
          </FormField>
          <FormField
            label="Emails sorted a month"
            htmlFor="calc-emails"
            tooltip={`Size for a busy month. Measured: ${inputs?.mailbox?.emails_measured ?? 0} emails in the last ${measured} days (${inputs?.mailbox?.emails_per_month ?? "none"} a month).`}
          >
            <Input id="calc-emails" value={emails} onChange={(event) => setEmails(event.target.value)} />
          </FormField>
          <FormField
            label="AI cost per email (US$)"
            htmlFor="calc-email-cost"
            tooltip={`Includes the occasional mailbox summary. This customer: ${inputs?.mailbox?.cost_per_email_usd ?? "none"}. All customers: ${inputs?.mailbox?.platform_cost_per_email_usd ?? "none"}.`}
          >
            <Input id="calc-email-cost" value={emailCost} onChange={(event) => setEmailCost(event.target.value)} />
          </FormField>
        </Cluster>
        <Cluster gap={3} align="center">
          <Button onClick={() => void saveDraft()} disabled={saving || !inputs}>
            {saving ? "Saving…" : "Save AI cost figures"}
          </Button>
        </Cluster>
        <table aria-label="Allowance results">
          <thead>
            <tr>
              <th>Service</th>
              <th>Provision</th>
              <th>Customer pays a month</th>
              <th>AI cost if fully used</th>
              <th>Margin at full use</th>
              <th>Cost limit to set</th>
              <th>Most the price carries at target</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>LeadGen</td>
              <td>{lead.units} leads a month</td>
              <td>{money(lead.priceCents, currency)}</td>
              <td>{formatAiUsd(lead.fullUseCostUsd)}</td>
              <td>{margin(lead, targetPct)}</td>
              <td>{money(lead.capCents, "USD")}</td>
              <td>{lead.maxUnitsAtMargin == null ? "Needs a cost per lead" : `${lead.maxUnitsAtMargin} leads`}</td>
            </tr>
            <tr>
              <td>Loxley</td>
              <td>{lox.units} requests per seat, {Math.floor(num(seats))} seats</td>
              <td>{money(lox.priceCents, currency)}</td>
              <td>{formatAiUsd(lox.fullUseCostUsd)}</td>
              <td>{margin(lox, targetPct)}</td>
              <td>{money(lox.capCents, "USD")} for all seats</td>
              <td>
                {lox.maxUnitsAtMargin == null ? "Needs a cost per request" : `${lox.maxUnitsAtMargin} requests per seat`}
              </td>
            </tr>
            <tr>
              <td>Mailbox AI</td>
              <td>{mail.units} emails a month</td>
              <td>{money(mail.priceCents, currency)}</td>
              <td>{formatAiUsd(mail.fullUseCostUsd)}</td>
              <td>{margin(mail, targetPct)}</td>
              <td>{money(mail.capCents, "USD")}</td>
              <td>{mail.maxUnitsAtMargin == null ? "Needs a cost per email" : `${mail.maxUnitsAtMargin} emails`}</td>
            </tr>
          </tbody>
        </table>
        <p className="admin-muted">
          {`Organization AI budget to cover these limits: ${money(suggestedBudget, "USD")}`}
          {inputs && orgId ? `. Currently ${money(inputs.budget.monthly_budget_cents, "USD")}.` : "."}
        </p>
      </Stack>
    </Card>
  );
}
